/**
 * SGNL-248 — Chart.js renderers for the Pipeline Pacing Dashboard.
 *
 * Three phase charts (Generation / Qualification / Conversion) + three
 * achievement donuts for the 4-week scorecard. Visual reference:
 * docs/screenshopts/part-1.png + part-2.png (Ketan's CY26 Success
 * Planning sheet).
 *
 * Each phase chart shows weekly Actual bars + the constant Target line
 * overlaid. Built-in Chart.js legend toggles each series; hover tooltip
 * surfaces week range + actual + target + delta. Donuts show
 * achievement % in the center with absolute totals in the tooltip.
 *
 * Lifecycle: the caller owns the canvas elements and keeps the returned
 * Chart instances so they can be destroyed on re-render. Mirrors the
 * SGNL-230 `_drillChart` pattern in revenue_calendar.ts.
 */

import { Chart, type ChartDataset } from "chart.js/auto";

import { fmtMoney } from "../lib/format";
import type { PipelinePacingPhase, PipelinePacingScorecard } from "../types";

// Bounteous-palette family per phase. Matches the tile colors used in
// the KPI strip + the weekly target callouts (pipeline_pacing_report.ts).
type PhaseFamily = "indigo" | "electric" | "aqua";

interface FamilyPalette {
  bar: string;
  line: string;
  grid: string;
  ring: string;
}

// ARCH-04 / CR-10 pre-PR: read brand colors from CSS custom properties
// at render time so styles.css remains the single source of truth.
// The hex fallbacks fire in environments without `document` (jsdom in
// some test paths, SSR previews) — they must stay in sync with the
// :root tokens. Tagged with the corresponding CSS variable name so a
// brand refresh has one place to change.
function _cssVar(name: string, fallback: string): string {
  if (typeof document === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

function _palette(family: PhaseFamily): FamilyPalette {
  const grid = _cssVar("--line", "#E5E7EB");
  switch (family) {
    case "indigo":
      // Generation — purple-rain bars, ink target line, indigo ring.
      return {
        bar: _cssVar("--purple-rain", "#853EFF"),
        line: _cssVar("--ink", "#0F172A"),
        grid,
        ring: _cssVar("--indigo", "#33139F"),
      };
    case "electric":
      // Qualification — electric-blue bars, dark-electric line + ring.
      return {
        bar: _cssVar("--electric-blue", "#34B4FF"),
        line: _cssVar("--electric-blue-dark", "#0E5DAA"),
        grid,
        ring: _cssVar("--electric-blue-dark", "#0E5DAA"),
      };
    case "aqua":
      // Conversion — aqua bars, selling-ai (deep teal) line + ring.
      return {
        bar: _cssVar("--aqua", "#2EEFEC"),
        line: _cssVar("--selling-ai", "#0E6F6E"),
        grid,
        ring: _cssVar("--selling-ai", "#0E6F6E"),
      };
  }
}

const PHASE_FAMILY: Record<PipelinePacingPhase["phase"], PhaseFamily> = {
  generation: "indigo",
  qualification: "electric",
  conversion: "aqua",
};

/**
 * Build a Chart.js bar chart for one phase. Bars = Actual; overlaid
 * dashed line = Target. Tooltip shows the week range + actual +
 * target + delta. Legend click toggles either series.
 *
 * Returns the Chart instance so the caller can `.destroy()` on re-render.
 */
export function renderPhaseChart(
  canvas: HTMLCanvasElement,
  phase: PipelinePacingPhase,
): Chart {
  // SEC-001 pre-PR: runtime guard against a BE that returns an
  // unexpected phase string. TS enforces the union at compile time
  // but the wire is the untrusted boundary. Unknown → indigo
  // (Generation palette) so the chart still renders rather than
  // throwing a TypeError that the caller's catch swallows silently.
  const family = PHASE_FAMILY[phase.phase] ?? "indigo";
  const palette = _palette(family);
  // Compact "May 26" labels — use the END-of-week Tuesday so the chart
  // matches Ketan's reference screenshot ("19-May, 26-May, 02-Jun…").
  // The tooltip's `title` callback restores the full range.
  const labels = phase.weeks.map((w) => _shortWeekLabel(w.week_ending));
  const actuals = phase.weeks.map((w) => w.actual);
  // Target may be null on every row (when budget unset). Chart.js renders
  // a flat line; null entries skip drawing without throwing.
  const targets = phase.weeks.map((w) => w.target);

  // Chart.js mixed-type chart — bars + overlaid line. ARCH-03/CR-07
  // pre-PR suggested `Chart<"bar" | "line">` as a cast-free path; in
  // practice Chart.js's line-dataset TData (number | [number, number]
  // | Point | BubbleDataPoint | null) is wider than bar's, so the
  // union-typed config rejects the bar dataset's narrower data array.
  // The `as ChartDataset<"bar", ...>` cast on the line dataset is the
  // ratified workaround per Chart.js GitHub discussions; it's
  // localised + commented so a future Chart.js bump can re-evaluate.
  //
  // Closure pattern (CR-11): the tooltip callbacks below close over
  // `phase` from this scope rather than reading from `ctx.chart.data`.
  // The destroy-on-rerender lifecycle (state.charts.forEach destroy
  // before any new mount) means a captured `phase` reference cannot
  // outlive its chart — every chart owns its own closure snapshot.
  // This is intentional vs the Chart.js doc's ctx-reading pattern;
  // simpler call sites in exchange for no shared mutable state.
  const datasets: ChartDataset<"bar", (number | null)[]>[] = [
    {
      label: "Actual",
      data: actuals,
      backgroundColor: palette.bar,
      borderRadius: 4,
      order: 2,
    },
    {
      type: "line",
      label: "Target",
      data: targets,
      borderColor: palette.line,
      backgroundColor: palette.line,
      borderDash: [6, 4],
      pointRadius: 3,
      tension: 0,
      fill: false,
      order: 1,
    } as unknown as ChartDataset<"bar", (number | null)[]>,
  ];

  return new Chart(canvas, {
    type: "bar",
    data: { labels, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: {
          position: "bottom",
          labels: { font: { size: 11 }, boxWidth: 12, padding: 12 },
        },
        tooltip: {
          callbacks: {
            title: (items) => {
              // Use the original week range string from `phase.weeks`,
              // not the compact x-axis label, so the tooltip carries
              // the full Tuesday→Tuesday detail. Guard against an
              // empty `items` array (PR-Agent importance 3) — the
              // previous `?? 0` default would show the wrong week
              // range when Chart.js fires a tooltip with no item.
              const i = items[0]?.dataIndex;
              if (i == null) return "";
              const w = phase.weeks[i];
              if (!w) return "";
              return `${w.week_starting} → ${w.week_ending}`;
            },
            label: (ctx) => {
              const v = ctx.parsed.y;
              return `${ctx.dataset.label}: ${fmtMoney(v as number | null)}`;
            },
            afterBody: (items) => {
              // Delta = Actual − Target. Skipped when either series is
              // toggled off or target is null.
              if (items.length < 2) return [];
              const actual = items.find((i) => i.dataset.label === "Actual")?.parsed.y;
              const target = items.find((i) => i.dataset.label === "Target")?.parsed.y;
              if (typeof actual !== "number" || typeof target !== "number") return [];
              const delta = actual - target;
              const sign = delta >= 0 ? "+" : "−";
              return [`Δ vs target: ${sign}${fmtMoney(Math.abs(delta))}`];
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { font: { size: 10 }, color: _cssVar("--slate", "#475569") },
        },
        y: {
          beginAtZero: true,
          grid: { color: palette.grid },
          ticks: {
            font: { size: 10 },
            color: _cssVar("--slate", "#475569"),
            callback: (val) => fmtMoney(val as number),
          },
        },
      },
    },
  });
}

/**
 * Donut chart for one phase's 4-week achievement scorecard.
 * Inner ring = achievement_pct (capped at 100% visually). Tooltip
 * shows the absolute actual + target + percentage.
 *
 * The donut renders even when achievement_pct is null (target unset);
 * in that case the ring shows neutral gray with "—%" in the tooltip.
 */
export function renderScorecardDonut(
  canvas: HTMLCanvasElement,
  scorecard: PipelinePacingScorecard,
): Chart {
  // SEC-001 pre-PR: same defensive lookup as renderPhaseChart.
  const family = PHASE_FAMILY[scorecard.phase] ?? "indigo";
  const palette = _palette(family);
  // SEC-002 pre-PR + PR-Agent importance 6: guard against NaN /
  // Infinity from a compromised or spoofed BE response. `hasPct` is
  // the single source of truth — it gates the clamp input, the ring
  // colour (gray when invalid, phase colour when valid), AND the
  // tooltip percentage (em-dash when invalid). The prior code only
  // applied the finite guard to the visual clamp; the tooltip + ring
  // still used the raw value, so a NaN/Infinity payload could render
  // "NaN%" / "Infinity%" in the tooltip alongside a valid-looking
  // phase ring.
  const raw = scorecard.achievement_pct;
  const hasPct = typeof raw === "number" && Number.isFinite(raw);
  const pct = hasPct ? raw : 0;
  // Cap the visual at 100% so over-achievers don't blow out the donut;
  // tooltip still shows the true number when valid.
  const visual = Math.min(100, Math.max(0, pct));

  return new Chart(canvas, {
    type: "doughnut",
    data: {
      labels: ["Achieved", "Remaining"],
      datasets: [
        {
          data: [visual, 100 - visual],
          backgroundColor: [
            hasPct ? palette.ring : _cssVar("--slate-300", "#CBD5E1"),
            _cssVar("--chart-track", "#F1F5F9"),
          ],
          borderWidth: 0,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      cutout: "70%",
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: () => {
              const actualLine = `Actual: ${fmtMoney(scorecard.actual_total)}`;
              const targetLine = `Target: ${fmtMoney(scorecard.target_total)}`;
              const pctLine = hasPct
                ? `Achievement: ${pct.toFixed(0)}%`
                : "Achievement: —";
              return [actualLine, targetLine, pctLine];
            },
          },
        },
      },
    },
  });
}

/** Cleanly tear down a previously-rendered chart. No-op for nulls so
 * callers can just call `destroyChart(maybeChart)` without guarding. */
export function destroyChart(c: Chart | null | undefined): void {
  c?.destroy();
}

// Internal: compact week label for the x-axis. "2026-05-19 → 2026-05-26"
// is too long for a tight chart; render "May 26" (week_ending) instead.
// The tooltip's `title` callback restores the full week range.
function _shortWeekLabel(iso: string): string {
  // Defensive: if the BE ever sends a malformed string, return the raw
  // input rather than crash the chart render. We `console.warn` so a
  // BE contract violation surfaces in DevTools/Sentry instead of
  // silently rendering an overlong x-axis label (ARCH-06 pre-PR).
  const parts = iso.split("-");
  if (parts.length !== 3) {
    // eslint-disable-next-line no-console
    console.warn(`[pacing-report] Malformed week label, expected ISO YYYY-MM-DD: ${iso}`);
    return iso;
  }
  const monthAbbr = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const m = parseInt(parts[1]!, 10);
  const d = parseInt(parts[2]!, 10);
  // TA-06 pre-PR: also reject day=0 (a JS Date with day=0 is the last
  // day of the previous month — almost certainly a BE bug, not a
  // valid input).
  if (Number.isNaN(m) || Number.isNaN(d) || m < 1 || m > 12 || d < 1 || d > 31) {
    // eslint-disable-next-line no-console
    console.warn(`[pacing-report] Malformed week label, out of range: ${iso}`);
    return iso;
  }
  return `${monthAbbr[m - 1]} ${d}`;
}

// Test seam — pure-function exposure of the label helper for vitest.
export const _testing = { shortWeekLabel: _shortWeekLabel };
